import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SearchIcon } from '../../../../assets/icons/search-icon.svg';
import { useAppSelector } from '../../../../redux/hooks';
import CustomDatePicker from '../../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ChargingSessionSidePanel from '../../../../shared-components/table-side-panel/ChargingSessionSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import emspSessionApi from '../../../../utils/api/v2/emsp-session';

interface SessionData {
  id: string;
  _id: string;
  chargingPointSn: string;
  chargingStation: string; // Station ID reference
  transactionStartsAt: string; // ISO date string
  transactionEndsAt: string; // ISO date string
  powerConsumedInKwh: string; // API returns string
  totalAmount: string; // API returns string
  chargingStationDetails: {
    _id: string;
    name: string;
  };
}

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
}
interface VehicleProperties {
  vehicle: string;
  type: string;
}

/**
 * SessionDetails component to show charging session history for an end consumer.
 * @param {any} param0 - The param0
 * @param {any} param0.vehicle - The vehicle prop
 * @param {any} param0.type - The type prop
 * @returns {any} JSX.Element
 */
function SessionDetails({ vehicle, type }: VehicleProperties) {
  const { t } = useTranslation('endConsumer');
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
  });

  const [filteredList, setFilteredList] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [sortData, setSortData] = useState({
    sortField: 'startTime',
    sortBy: 'desc',
  });

  /**
   * Fetch session list from API
   */
  const fetchSessions = useCallback(
    async (userCustomId: string, search?: string) => {
      setLoading(true);
      try {
        const query: any = {};

        if (dateRange.startDate) {
          query.startDate = dayjs(dateRange.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        if (dateRange.endDate) {
          query.endDate = dayjs(dateRange.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        // ✅ ADD SEARCH PARAM
        if (search && search.trim() !== '') {
          query.searchBy = search.trim();
        }
        query.vehicleId = vehicle;
        query.userCustomId = userCustomId;
        const response =
          type === 'personal'
            ? await emspSessionApi.fetchPersonalSessionList(query)
            : await emspSessionApi.fetchCommercialSessionVehicalList(query);
        const resourcesData: any = response?.data;
        let data: any[] = [];
        if (Array.isArray(resourcesData?.results)) {
          data = resourcesData.results;
        } else if (Array.isArray(resourcesData)) {
          data = resourcesData;
        } else if (Array.isArray(resourcesData?.data)) {
          data = resourcesData.data;
        }

        setFilteredList(data);
      } catch (error) {
        console.error('Failed to fetch session list:', error);
      } finally {
        setLoading(false);
      }
    },
    [dateRange, vehicle],
  );

  /**
   * Handles updating the search term state from CustomInput
   * @param {any} name - Field name
   * @param {any} value - Input value (string or number)
   */
  const handleSearchChange = (name: string, value: string | number): void => {
    setSearchTerm(String(value));
  };

  /**
   * Handle date range change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleDateChange = (name: string, value: string) => {
    setDateRange((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle sort
   * @param {any} newSortData - The newSortData
   */
  const handleSortChange = (newSortData: any) => {
    setSortData(newSortData);
  };

  /**
   * Handle open charge session details
   * @param {any} rowData - The session data
   */
  const handleOpenChargeSessionDetails = (rowData: any) => {
    const sessionId = rowData?.sessionId || rowData?._id || rowData?.id;
    if (!sessionId) return;
    setIsOpen(true);
    setModelDetails({
      title: t('chargingSessionDetails'),
      content: <ChargingSessionSidePanel fetchIdFrom="props" id={sessionId} />,
    });
  };

  const columns: any[] = [
    {
      label: t('dateTime'),
      accessorKey: 'startTime',
      sortAllow: true,
      /**
       * SessionData
       * @param {any} currentRow - Current row data
       * @returns {any} - Custom data
       */
      customData: (currentRow: any) => {
        const startIso =
          currentRow?.sessionStartAt ||
          currentRow?.transactionStartsAt ||
          currentRow?.createdAt ||
          currentRow?.startedAt;
        return (
          <div>
            <p>{startIso ? ConvertUTCtoDate(startIso) : '-'}</p>
            <p>{startIso ? convertUtcTo12HourTime(startIso) : ''}</p>
          </div>
        );
      },
    },
    {
      label: t('station'),
      accessorKey: 'chargingPointSn',
      sortAllow: true,
      /**
       * Rendering station name with hyper link
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: any) => (
        <p role="presentation" className="hyper-link">
          {currentRow?.chargingStationDetails?.name ||
            currentRow?.station?.name ||
            currentRow?.locationId ||
            currentRow?.chargingStation ||
            '-'}
        </p>
      ),
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn',
      sortAllow: true,
      /**
       * Rendering device name with hyper link
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: any) => (
        <p role="presentation" className="hyper-link">
          {currentRow?.chargingPointSn ||
            currentRow?.evseUid ||
            currentRow?.userCustomId ||
            currentRow?.station?.deviceNo ||
            '-'}
        </p>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed',
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: any) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenChargeSessionDetails(currentRow)}>
          {ToFixedValue(
            Number(
              currentRow?.powerConsumedInKwh ??
                currentRow?.energyKwh ??
                currentRow?.powerConsumed ??
                0,
            ),
            3,
          )}{' '}
          kWh
        </p>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount',
      sortAllow: true,
      /**
       * SessionData
       * @param {any} row - Row data
       * @returns {any} - Amount display
       */
      customData: (row: any) => (
        <p className="amount-text">
          {ToFixedValue(
            row?.totalAmount ?? row?.cost?.total ?? row?.total ?? row?.amount ?? 0,
            2,
          )}
        </p>
      ),
    },
  ];

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const delayDebounce = setTimeout(() => {
      fetchSessions(selectedEndConsumer.userCustomId, searchTerm);
    }, 500); // wait 500ms after typing stops

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, dateRange, selectedEndConsumer, fetchSessions]);

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      fetchSessions(selectedEndConsumer.userCustomId);
    }
  }, [fetchSessions, selectedEndConsumer]);

  return (
    <div className="session-details-wrap-v2">
      <div className="session-details-filters">
        <div className="search-filter">
          <CustomInput
            type="text"
            name="search"
            placeholder={t('searchHere')}
            value={searchTerm}
            onChangeValue={handleSearchChange}
            svgIcon={<SearchIcon />}
          />
        </div>
        <div className="date-filters">
          <CustomDatePicker
            name="startDate"
            value={dateRange.startDate}
            onChangeValue={handleDateChange}
            placeholder="DD/MM/YYYY"
          />
          <CustomDatePicker
            name="endDate"
            value={dateRange.endDate}
            onChangeValue={handleDateChange}
            placeholder="DD/MM/YYYY"
          />
        </div>
      </div>

      <div className="session-details-table-wrap">
        <SidePaneModel
          open={isOpen}
          title={modelDetails.title}
          content={modelDetails.content}
          closePanel={handleCloseModal}
          url={modelDetails.url}
        />
        <CustomTable
          header={columns}
          data={filteredList}
          loading={loading}
          serialNo
          version="v1"
          height={window.innerHeight >= 700 ? 320 : 322}
          sortData={sortData}
          handleSortChange={handleSortChange}
          disableHeader
          disablePagination
        />
      </div>
    </div>
  );
}

export default SessionDetails;
